import React, { useCallback, useEffect, useRef } from 'react';
import ym from 'react-yandex-metrika';

import b from 'b_';
import { Refresh } from 'education-icons';
import {
    Button,
    Input,
    Modal,
    Popup,
    SimpleText,
    Spin,
    Text,
    Tooltip,
    SimpleCallback,
    H3,
    ModalCloseProps,
} from 'platform-components';
import { POPUP_AXIS, POPUP_DIRECTIONS, SPIN_SIZES } from 'platform-components/constants';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';

import { useTeamLabel } from 'utils/get-name';

import statefulContainer from './stateful-container';

import './invite-teacher-modal.scss';

interface Props {
    teamGrade?: number;
    teamId: number;
    code: string;
    link: string;
    copyCode: SimpleCallback;
    copyLink: SimpleCallback;
    generateCode: SimpleCallback;
    generateLink: SimpleCallback;
    isLoading: boolean;
    onRequestClose: SimpleCallback;
    open: boolean;
    shouldShowGenerateCodePopup: boolean;
    shouldShowCopyCodePopup: boolean;
    shouldShowCopyLinkPopup: boolean;
    shouldShowGenerateLinkPopup: boolean;
}

// Presentation component
const InviteTeacherModalComponent = ({
    teamGrade,
    teamId,
    code,
    copyCode,
    copyLink,
    generateCode,
    generateLink,
    isLoading,
    link,
    open,
    onRequestClose,
    shouldShowGenerateCodePopup,
    shouldShowCopyCodePopup,
    shouldShowCopyLinkPopup,
    shouldShowGenerateLinkPopup,
}: Props) => {
    const generatePopupRef = useRef<Popup | null>(null);
    const copyPopupRef = useRef<Popup | null>(null);
    const copyLinkPopupRef = useRef<Popup | null>(null);
    const generateLinkPopupRef = useRef<Popup | null>(null);

    useEffect(() => {
        if (shouldShowGenerateCodePopup && generatePopupRef && generatePopupRef.current) {
            generatePopupRef.current.show();
        }

        if (!shouldShowGenerateCodePopup && generatePopupRef && generatePopupRef.current) {
            generatePopupRef.current.hide();
        }
    }, [shouldShowGenerateCodePopup]);

    useEffect(() => {
        if (shouldShowCopyCodePopup && copyPopupRef && copyPopupRef.current) {
            copyPopupRef.current.show();
        }

        if (!shouldShowCopyCodePopup && copyPopupRef && copyPopupRef.current) {
            copyPopupRef.current.hide();
        }
    }, [shouldShowCopyCodePopup]);

    useEffect(() => {
        if (shouldShowCopyLinkPopup && copyLinkPopupRef && copyLinkPopupRef.current) {
            copyLinkPopupRef.current.show();
        }

        if (!shouldShowCopyLinkPopup && copyPopupRef && copyLinkPopupRef.current) {
            copyLinkPopupRef.current.hide();
        }
    }, [shouldShowCopyLinkPopup]);

    useEffect(() => {
        if (shouldShowGenerateLinkPopup && generateLinkPopupRef && generateLinkPopupRef.current) {
            generateLinkPopupRef.current.show();
        }

        if (!shouldShowGenerateLinkPopup && generateLinkPopupRef && generateLinkPopupRef.current) {
            generateLinkPopupRef.current.hide();
        }
    }, [shouldShowGenerateLinkPopup]);

    const handleGenerateBtnCLick = useCallback(() => {
        ym('reachGoal', 'teacher_my_students_class_new_class_code', {
            team_id: teamId,
        });

        generateCode();
    }, [teamId, generateCode]);

    const teamLabel = useTeamLabel({ grade: teamGrade, teamLabelCase: 'вин' });

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="invite-teacher-modal"
            onRequestClose={onRequestClose}
            opened={open}
            theme={Modal.THEMES.TEACHER}
        >
            <Modal.Header mix="qa-invite-teacher-modal-header">
                <H3 id="class.inviteTeacherModal.header" values={{ teamLabel }} weight={Text.WEIGHT.M} />
            </Modal.Header>
            <Modal.Content mix="invite-teacher-modal__content">
                <div className="invite-teacher-modal__description">
                    <Text id="class.inviteTeacherModal.description" type={Text.TYPE.MARKDOWN} />
                </div>
                <div className="invite-teacher-modal__code-plate">
                    <div className={b('invite-teacher-modal', 'code', { disabled: isLoading })}>
                        <Text size={Text.SIZE.XXL}>{code}</Text>
                    </div>
                    <div className="invite-teacher-modal__copy-block">
                        <Button className="invite-teacher-modal__btn-copy" disabled={isLoading} onClick={copyCode}>
                            <SimpleText id="class.inviteTeacherModal.copyBtn" />
                        </Button>
                        <div className="invite-teacher-modal__popup-wrapper">
                            <Tooltip
                                //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
                                onPopupRef={copyPopupRef}
                                popupProps={{
                                    autoclosable: true,
                                    selfclosable: true,
                                    shown: false,
                                    axis: POPUP_AXIS.CENTER,
                                    offsetTop: 0,
                                    direction: POPUP_DIRECTIONS.RIGHT,
                                }}
                            >
                                <Text id="class.inviteTeacherModal.codeCopied" />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="invite-teacher-modal__generate-block">
                        <Button
                            className="invite-teacher-modal__btn-generate"
                            onClick={handleGenerateBtnCLick}
                            progress={isLoading}
                            view={Button.VIEW.SECONDARY}
                        >
                            <SimpleText id="class.inviteTeacherModal.generateCodeBtn" />
                        </Button>
                        <div className="invite-teacher-modal__popup-wrapper">
                            <Tooltip
                                //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
                                onPopupRef={generatePopupRef}
                                popupProps={{
                                    autoclosable: true,
                                    selfclosable: true,
                                    shown: false,
                                    axis: POPUP_AXIS.CENTER,
                                    offsetTop: 0,
                                    direction: POPUP_DIRECTIONS.RIGHT,
                                }}
                            >
                                <Text id="class.inviteTeacherModal.codeCreated" />
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className="invite-teacher-modal__link-block">
                    <div className="invite-teacher-modal__divider">
                        <span className="invite-teacher-modal__divider-title">
                            <Text id="class.inviteTeacherModal.orDivider" />
                        </span>
                    </div>
                    <div className="invite-teacher-modal__link-description">
                        <Text id="class.inviteTeacherModal.linkDescription" values={{ teamLabel }} />
                    </div>
                    <div className="invite-teacher-modal__link-copy-block">
                        <div className="invite-teacher-modal__input-wrapper">
                            <Input mix="invite-teacher-modal__link-input" value={link} />
                            {isLoading ? (
                                <Spin mix="invite-teacher-modal__refresh-spiner" size={SPIN_SIZES.XS} />
                            ) : (
                                <Refresh className="invite-teacher-modal__refresh-icon" onClick={generateLink} />
                            )}
                        </div>
                        <Button
                            className="invite-teacher-modal__copy-link-btn"
                            disabled={isLoading}
                            onClick={copyLink}
                            view={Button.VIEW.SECONDARY}
                        >
                            <SimpleText id="class.inviteTeacherModal.copyLink" />
                        </Button>
                        <div className="invite-teacher-modal__popup-wrapper">
                            <Tooltip
                                //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
                                onPopupRef={copyLinkPopupRef}
                                popupProps={{
                                    autoclosable: true,
                                    selfclosable: true,
                                    shown: false,
                                    axis: POPUP_AXIS.CENTER,
                                    offsetTop: 0,
                                    direction: POPUP_DIRECTIONS.RIGHT,
                                }}
                            >
                                <Text id="class.inviteTeacherModal.linkCopied" />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="invite-teacher-modal__popup-wrapper_bottom">
                        <Tooltip
                            //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
                            onPopupRef={generateLinkPopupRef}
                            popupProps={{
                                autoclosable: true,
                                selfclosable: true,
                                shown: false,
                                axis: POPUP_AXIS.START,
                                offsetTop: 0,
                                direction: POPUP_DIRECTIONS.BOTTOM,
                            }}
                        >
                            <Text id="class.inviteTeacherModal.linkGenerated" />
                        </Tooltip>
                    </div>
                </div>
            </Modal.Content>
        </Modal>
    );
};

export const InviteTeacherModal = statefulContainer(InviteTeacherModalComponent);
